import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'

interface WorkCardProps {
    project: ProjectSummary
    className?: string
}

// Hover and focus: the cover fades to the second image, which settles from a slight zoom, and the title rises into view.
// Every moving part is opacity or transform, so the effect stays on the compositor.
export const WorkCard: FC<WorkCardProps> = ({ project, className }): ReactNode => {
    const hoverImage = project.imageOne ?? project.cover

    return (
        <Link
            href={`/work/${project.slug}`}
            className={cn('group/card flex w-full flex-col gap-4', className)}
        >
            <div className='relative aspect-[1.41562] w-full overflow-clip bg-muted'>
                {hoverImage && (
                    <img
                        src={hoverImage}
                        alt=''
                        loading='lazy'
                        decoding='async'
                        className='absolute inset-0 size-full scale-105 object-cover object-[50%_35%] transition-transform duration-slow ease-studio group-hover/card:scale-100 group-focus-visible/card:scale-100'
                    />
                )}

                {project.cover && (
                    <img
                        src={project.cover}
                        alt={project.coverAlt ?? project.client}
                        loading='lazy'
                        decoding='async'
                        className={cn(
                            'absolute inset-0 z-[1] size-full object-cover object-[50%_35%] transition-opacity duration-slow ease-studio',
                            project.imageOne && 'group-hover/card:opacity-0 group-focus-visible/card:opacity-0'
                        )}
                    />
                )}

                <div
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-0 transition-opacity duration-base ease-studio group-hover/card:opacity-100 group-focus-visible/card:opacity-100'
                />

                <div className='absolute inset-x-10 bottom-5 z-[2] flex translate-y-6 flex-col items-center gap-2.5 opacity-0 transition-[transform,opacity] duration-base ease-studio group-hover/card:translate-y-0 group-hover/card:opacity-100 group-focus-visible/card:translate-y-0 group-focus-visible/card:opacity-100'>
                    <h3
                        data-mdlfy-cms={`projects:${project.id}:title`}
                        className='t-title text-center text-cream'
                    >
                        {project.title}
                    </h3>
                </div>
            </div>

            <div className='flex w-full items-start gap-6 text-foreground'>
                <p
                    data-mdlfy-cms={`projects:${project.id}:client`}
                    className='t-italic flex-1 text-left'
                >
                    {project.client}
                </p>

                <p
                    data-mdlfy-cms={`projects:${project.id}:year`}
                    className='t-italic whitespace-nowrap'
                >
                    {project.year}
                </p>
            </div>
        </Link>
    )
}
